import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  KnowledgeFacetAggregate,
  KnowledgeSearchHit,
} from '@digital-twins/shared-types'
import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { PageContent } from '@/components/layout/PageContent'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useAuth } from '@/lib/config/auth-provider'
import { PYLON_CONTACT_SUPPORT_URL } from '@/lib/constants/pylon'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { knowledgeApi, KnowledgeDocumentWithStages } from '@/lib/api'
import { onKnowledgeLinkIngestUpdated } from '@/lib/services/websocket'
import { applyKnowledgeIndexingEventToDocuments } from './applyKnowledgeIndexingEvent'
import { useKnowledgeDocumentIndexingUpdates } from './useKnowledgeDocumentIndexingUpdates'
import { KnowledgeDocumentCollection } from './KnowledgeDocumentCollection'
import { KnowledgeLibrarySearch } from './KnowledgeLibrarySearch'
import { KnowledgeLibraryBand } from './KnowledgeLibraryBand'
import { KnowledgeSearchResults } from './KnowledgeSearchResults'
import {
  documentMatchesFacetFilters,
  documentTypeLabel,
  filtersFromSearchParams,
  isQuestionShaped,
} from './libraryHelpers'
import { KnowledgeAddLinksModal } from './KnowledgeAddLinksModal'
import { KnowledgeDocumentModal } from '../KnowledgeDocumentPage/KnowledgeDocumentModal'
import { KnowledgeLibraryHeader } from './KnowledgeLibraryHeader'
import { SocialTrackerDialog } from './social/SocialTrackerDialog'
import { SocialMediaTab } from './social/SocialMediaTab'
import { useSocialTrackers } from './social/useSocialTrackers'

const LIBRARY_VIEW_PARAM = 'view'
const LIBRARY_SOCIAL_VIEW = 'social'
const SOCIAL_TRACKER_PARAM = 'tracker'

export default function KnowledgeHubPage() {
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const { isLoading: isAuthLoading } = useAuth()
  const canLoadLibrary = !isAuthLoading && assistantPlatformEnabled
  const isLibraryLocked = !isAuthLoading && !assistantPlatformEnabled
  const socialTrackers = useSocialTrackers(canLoadLibrary)
  const isSocialEnabled = canLoadLibrary && socialTrackers.isEnabled
  const isSocialView =
    isSocialEnabled &&
    searchParams.get(LIBRARY_VIEW_PARAM) === LIBRARY_SOCIAL_VIEW
  const [documents, setDocuments] = useState<
    Array<KnowledgeDocumentWithStages>
  >([])
  const [facetCatalog, setFacetCatalog] = useState<
    Array<KnowledgeFacetAggregate>
  >([])
  const [loading, setLoading] = useState(true)
  const [deletingToken, setDeletingToken] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchHits, setSearchHits] = useState<Array<KnowledgeSearchHit>>([])
  const [searching, setSearching] = useState(false)
  const [activeType, setActiveType] = useState<string | null>(null)
  const [filters, setFilters] = useState<Record<string, string | null>>({})
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [addLinksOpen, setAddLinksOpen] = useState(false)
  const [addTrackerOpen, setAddTrackerOpen] = useState(false)
  const [openDocToken, setOpenDocToken] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window === 'undefined') return 'grid'
    return window.sessionStorage.getItem('knowledge-library-view') === 'list'
      ? 'list'
      : 'grid'
  })
  const loadGenerationRef = useRef(0)

  async function loadLibrary(options?: { quiet?: boolean }) {
    const generation = ++loadGenerationRef.current
    if (!options?.quiet) {
      setLoading(true)
    }
    try {
      const [list, facetsResponse] = await Promise.all([
        knowledgeApi.listDocuments(),
        knowledgeApi
          .listFacets()
          .catch(() => ({ facets: [], documentCount: 0 })),
      ])
      if (generation !== loadGenerationRef.current) {
        return
      }
      setDocuments(list)
      setFacetCatalog(facetsResponse.facets)
    } catch (error: unknown) {
      if (generation !== loadGenerationRef.current) {
        return
      }
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('library.loadFailed')
      toast.error(message)
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  function handleDocumentUpdated(updated: KnowledgeDocumentWithStages) {
    setDocuments((prev) =>
      prev.map((doc) => (doc.token === updated.token ? updated : doc)),
    )
  }

  useKnowledgeDocumentIndexingUpdates(
    (event) => {
      setDocuments((prev) => {
        const result = applyKnowledgeIndexingEventToDocuments(prev, event)
        if (result.missingDocument) {
          void loadLibrary({ quiet: true })
          return prev
        }
        return result.documents
      })
    },
    { enabled: canLoadLibrary },
  )

  useEffect(() => {
    if (!canLoadLibrary) {
      return
    }
    return onKnowledgeLinkIngestUpdated((event) => {
      if (event.status === 'failed') {
        void loadLibrary({ quiet: true })
        return
      }
      if (event.status === 'created') {
        void loadLibrary({ quiet: true })
      }
    })
  }, [canLoadLibrary])

  useEffect(() => {
    window.sessionStorage.setItem('knowledge-library-view', viewMode)
  }, [viewMode])

  useEffect(() => {
    if (!canLoadLibrary) {
      return
    }
    void loadLibrary()
  }, [canLoadLibrary])

  // Deep-link: /knowledge?ask=1&q=...&doc=token → Threads
  useEffect(() => {
    const ask = searchParams.get('ask')
    const q = searchParams.get('q')
    const doc = searchParams.get('doc')
    const fromUrl = filtersFromSearchParams(searchParams)

    if (Object.keys(fromUrl).length > 0) {
      setFilters((prev) => ({ ...prev, ...fromUrl }))
      if (fromUrl.type) {
        setActiveType(fromUrl.type)
      }
    }
    if (q && ask !== '1' && ask !== 'true' && !doc) {
      setSearchQuery(q)
    }
    if (ask === '1' || ask === 'true' || doc) {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (doc) params.set('doc', doc)
      const qs = params.toString()
      navigate(qs ? `/threads?${qs}` : '/threads', { replace: true })
    }
  }, [searchParams, navigate])

  useEffect(() => {
    if (!canLoadLibrary) {
      setSearchHits([])
      setSearching(false)
      return
    }
    const q = searchQuery.trim()
    if (q.length < 2 || isQuestionShaped(q)) {
      setSearchHits([])
      setSearching(false)
      return
    }
    setSearching(true)
    let cancelled = false
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await knowledgeApi.search(q)
          if (!cancelled) {
            setSearchHits(result.hits)
          }
        } catch {
          if (!cancelled) {
            setSearchHits([])
          }
        } finally {
          if (!cancelled) setSearching(false)
        }
      })()
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [searchQuery, canLoadLibrary])

  async function handleDelete(token: string) {
    setDeletingToken(token)
    try {
      await knowledgeApi.deleteDocument(token)
      setDocuments((prev) => prev.filter((doc) => doc.token !== token))
      toast.success(t('library.deleted'))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? t('library.deleteFailed')
      toast.error(message)
    } finally {
      setDeletingToken(null)
    }
  }

  function goToThreadsAsk(seed?: string, docToken?: string | null) {
    const params = new URLSearchParams()
    if (seed) params.set('q', seed)
    if (docToken) params.set('doc', docToken)
    const qs = params.toString()
    navigate(qs ? `/threads?${qs}` : '/threads')
  }

  function handleLibraryViewChange(view: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (view === LIBRARY_SOCIAL_VIEW) {
        next.set(LIBRARY_VIEW_PARAM, LIBRARY_SOCIAL_VIEW)
      } else {
        next.delete(LIBRARY_VIEW_PARAM)
        next.delete(SOCIAL_TRACKER_PARAM)
      }
      return next
    })
  }

  function handleOpenTrackerChange(token: string | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (token) {
        next.set(SOCIAL_TRACKER_PARAM, token)
      } else {
        next.delete(SOCIAL_TRACKER_PARAM)
      }
      return next
    })
  }

  const typeTabs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const doc of documents) {
      const label = documentTypeLabel(doc)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    const tabs = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([label, count]) => ({ key: label, label, count }))
    return [{ key: 'all', label: 'All', count: documents.length }, ...tabs]
  }, [documents])

  const ownerOptions = useMemo(() => {
    const counts = new Map<string, number>()
    for (const doc of documents) {
      const name = doc.ownerName?.trim()
      if (!name) continue
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, count }))
  }, [documents])

  const facetFilters = useMemo(() => {
    const next: Record<string, string | null> = { ...filters }
    if (activeType) next.type = activeType
    else delete next.type
    return next
  }, [filters, activeType])

  const filteredDocuments = useMemo(() => {
    let list = documents.filter((d) =>
      documentMatchesFacetFilters(d, facetFilters),
    )
    const q = searchQuery.trim()
    if (q.length >= 2 && !isQuestionShaped(q) && searchHits.length > 0) {
      const hitTokens = new Set(searchHits.map((h) => h.token))
      list = list.filter((d) => hitTokens.has(d.token))
    } else if (
      q.length >= 2 &&
      !isQuestionShaped(q) &&
      !searching &&
      searchHits.length === 0
    ) {
      list = []
    }
    return list
  }, [documents, facetFilters, searchQuery, searchHits, searching])

  const hasActiveQueryOrFilters =
    (searchQuery.trim().length > 0 && !isQuestionShaped(searchQuery)) ||
    activeType != null ||
    Object.values(filters).some(Boolean)

  const showSearchResults =
    !isLibraryLocked &&
    searchQuery.trim().length >= 2 &&
    !isQuestionShaped(searchQuery)

  const showDocumentSkeleton = isAuthLoading || (canLoadLibrary && loading)

  return (
    <Tabs
      value={isSocialView ? LIBRARY_SOCIAL_VIEW : 'docs'}
      onValueChange={handleLibraryViewChange}
      className="min-h-screen bg-background"
    >
      <PageLayout className="max-w-none gap-5 p-6 md:p-8">
        <KnowledgeLibraryHeader
          socialEnabled={isSocialEnabled}
          view={isSocialView ? 'social' : 'docs'}
          canLoadLibrary={canLoadLibrary}
          canManageTrackers={socialTrackers.canManage}
          onAddLinks={() => setAddLinksOpen(true)}
          onAddTracker={() => setAddTrackerOpen(true)}
        />
        <PageContent className="w-full gap-4">
          {isLibraryLocked ? (
            <LobbyEmptyState
              title={t('library.locked.title')}
              description={t('library.locked.body')}
            >
              <div data-testid="knowledge-locked-state">
                <Button asChild variant="primary">
                  <a
                    href={PYLON_CONTACT_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="knowledge-locked-contact-support"
                  >
                    {t('home.locked.contactSupport')}
                  </a>
                </Button>
              </div>
            </LobbyEmptyState>
          ) : (
            <>
              {isSocialView ? (
                <TabsContent
                  value="social"
                  className="m-0 focus-visible:outline-none"
                >
                  <SocialMediaTab
                    showHeader={false}
                    canAddTracker={false}
                    trackers={socialTrackers.trackers}
                    canManage={socialTrackers.canManage}
                    isLoading={socialTrackers.isLoading}
                    openTrackerToken={searchParams.get(SOCIAL_TRACKER_PARAM)}
                    onOpenTrackerChange={handleOpenTrackerChange}
                    onTrackerChanged={socialTrackers.replaceTracker}
                    onTrackerDeleted={socialTrackers.removeTracker}
                    onRefresh={() => void socialTrackers.refresh()}
                  />
                </TabsContent>
              ) : (
                <TabsContent
                  value="docs"
                  aria-label={t('socialListening.tabs.myDocs')}
                  className="m-0 flex flex-col gap-4 focus-visible:outline-none"
                >
                  <KnowledgeLibrarySearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onAsk={goToThreadsAsk}
                  />

                  <KnowledgeLibraryBand
                    typeTabs={typeTabs}
                    activeType={activeType}
                    onTypeChange={setActiveType}
                    facetCatalog={facetCatalog}
                    filters={filters}
                    onFilterChange={(kind, value) =>
                      setFilters((prev) => ({ ...prev, [kind]: value }))
                    }
                    showFilterPanel={showFilterPanel}
                    onToggleFilterPanel={() => setShowFilterPanel((v) => !v)}
                    resultCount={
                      hasActiveQueryOrFilters ? filteredDocuments.length : null
                    }
                    ownerOptions={ownerOptions}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                  />

                  {showSearchResults ? (
                    <KnowledgeSearchResults
                      query={searchQuery.trim()}
                      hits={searchHits.filter((h) => {
                        const doc = documents.find((d) => d.token === h.token)
                        if (!doc) return true
                        return documentMatchesFacetFilters(doc, facetFilters)
                      })}
                      searching={searching}
                      documents={documents}
                    />
                  ) : (
                    <KnowledgeDocumentCollection
                      showDocumentSkeleton={showDocumentSkeleton}
                      filteredDocuments={filteredDocuments}
                      hasActiveQueryOrFilters={hasActiveQueryOrFilters}
                      viewMode={viewMode}
                      deletingToken={deletingToken}
                      onDelete={(token) => void handleDelete(token)}
                      onDocumentUpdated={handleDocumentUpdated}
                      onOpen={setOpenDocToken}
                      onAddLinks={() => setAddLinksOpen(true)}
                      onClearFilters={() => {
                        setSearchQuery('')
                        setActiveType(null)
                        setFilters({})
                      }}
                    />
                  )}
                </TabsContent>
              )}
            </>
          )}
        </PageContent>
      </PageLayout>
      <SocialTrackerDialog
        open={addTrackerOpen}
        tracker={null}
        onOpenChange={setAddTrackerOpen}
        onSaved={socialTrackers.replaceTracker}
      />
      <KnowledgeAddLinksModal
        open={addLinksOpen}
        onOpenChange={setAddLinksOpen}
        onDocumentsChanged={() => void loadLibrary({ quiet: true })}
      />
      {openDocToken ? (
        <KnowledgeDocumentModal
          documentToken={openDocToken}
          onClose={() => setOpenDocToken(null)}
          onDocumentUpdated={handleDocumentUpdated}
          onDeleted={(token) => {
            setDocuments((prev) => prev.filter((doc) => doc.token !== token))
          }}
        />
      ) : null}
    </Tabs>
  )
}
